import React, { useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Text,
  VStack,
  Divider,
  useToast,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { FaEnvelope } from 'react-icons/fa';
import { standardizeError } from '../../utils';

const cilColors = {
  yellow: '#ffcc00',
  yellowDark: '#e6b800',
  plum: '#3b235a',
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Form nel modale progetto: invia email a CIL con oggetto = titolo progetto.
 */
const SponsorProjectInquiryForm = ({ project }) => {
  const toast = useToast();
  const [email, setEmail] = useState('');
  const [recapito, setRecapito] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  if (!project?.title) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedEmail = email.trim();
    const trimmedRecapito = recapito.trim();

    if (!trimmedEmail || !EMAIL_RE.test(trimmedEmail)) {
      toast({
        title: 'Email non valida',
        description: 'Inserisci un indirizzo email valido.',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    if (!trimmedRecapito) {
      toast({
        title: 'Recapito obbligatorio',
        description: 'Indica un telefono o un altro modo per ricontattarti.',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch('/api/projects/sponsor-inquiry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: trimmedEmail,
          recapito: trimmedRecapito,
          message: message.trim(),
          projectTitle: project.title,
          projectDocumentId: project.documentId || project.id || null,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Invio non riuscito');
      }
      setSubmitted(true);
      setEmail('');
      setRecapito('');
      setMessage('');
      toast({
        title: 'Richiesta inviata',
        description: 'Ti ricontatteremo al più presto.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (err) {
      const standardized = standardizeError(err);
      toast({
        title: 'Errore',
        description:
          standardized.message || 'Impossibile inviare la richiesta. Riprova.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <Alert status="success" borderRadius="md" mt={4}>
        <AlertIcon />
        <Text fontSize="sm">
          Grazie! Abbiamo ricevuto la tua richiesta per questo progetto e ti
          ricontatteremo presto.
        </Text>
      </Alert>
    );
  }

  return (
    <Box as="form" onSubmit={handleSubmit} mt={{ base: 4, md: 5 }}>
      <Divider mb={4} />
      <Text
        fontSize={{ base: 'sm', md: 'md' }}
        fontWeight="bold"
        color={cilColors.plum}
        mb={3}
      >
        Ti interessa sostenere questo progetto?
      </Text>
      <VStack spacing={4} align="stretch">
        <FormControl isRequired>
          <FormLabel fontSize="sm" mb={1.5}>
            La tua email
          </FormLabel>
          <Input
            type="email"
            name="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="nome@esempio.it"
            size="md"
            minH="44px"
            autoComplete="email"
          />
        </FormControl>
        <FormControl isRequired>
          <FormLabel fontSize="sm" mb={1.5}>
            Recapito telefonico
          </FormLabel>
          <Input
            type="tel"
            name="recapito"
            value={recapito}
            onChange={(e) => setRecapito(e.target.value)}
            placeholder="es. 333 1234567"
            size="md"
            minH="44px"
            autoComplete="tel"
          />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm" mb={1.5}>
            Messaggio (opzionale)
          </FormLabel>
          <Textarea
            name="message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Raccontaci come vorresti collaborare…"
            rows={4}
            size="md"
          />
        </FormControl>
        <Button
          type="submit"
          size="md"
          w="full"
          minH="48px"
          bg={cilColors.yellow}
          color={cilColors.plum}
          fontWeight="bold"
          borderRadius="lg"
          leftIcon={<FaEnvelope />}
          _hover={{ bg: cilColors.yellowDark }}
          isLoading={isSubmitting}
          loadingText="Invio…"
        >
          Invia richiesta
        </Button>
      </VStack>
    </Box>
  );
};

export default SponsorProjectInquiryForm;
